import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import CONFIG from '../../config';
import AsistenciaDetalleEstudiante from '../estudiantes/AsistenciaDetalleEstudiante';
import PerfilLogo from '../usuarios/PerfilLogo';
import Espera from '../Espera';

const URL_REPORTES = `${CONFIG.API_URL}/operarReportes.php`;
const URL_LISTAR = `${CONFIG.API_URL}/listarUsuarios.php`;

function InasistenciasEstudiantePreceptor({ acceder, rol, configuracion }) {
    const navigate = useNavigate();
    const rolNum = Number(rol);

    const [modoBusqueda, setModoBusqueda] = useState('estudiante'); // 'estudiante' | 'curso'
    const [estudianteSeleccionado, setEstudianteSeleccionado] = useState(null);

    // Estado para búsqueda por estudiante (nombre / DNI)
    const [busquedaTexto, setBusquedaTexto] = useState('');
    const [todosEstudiantes, setTodosEstudiantes] = useState([]);
    const [loadingEstudiantesGeneral, setLoadingEstudiantesGeneral] = useState(false);

    // Estado para búsqueda por curso
    const [combinaciones, setCombinaciones] = useState([]);
    const [filtroCiclo, setFiltroCiclo] = useState('');
    const [filtroNivel, setFiltroNivel] = useState('');
    const [filtroOrden, setFiltroOrden] = useState('');
    const [filtroSeccion, setFiltroSeccion] = useState('');

    const [opcionesCiclos, setOpcionesCiclos] = useState([]);
    const [opcionesNiveles, setOpcionesNiveles] = useState([]);
    const [opcionesOrdenes, setOpcionesOrdenes] = useState([]);
    const [opcionesSecciones, setOpcionesSecciones] = useState([]);

    const [estudiantesCurso, setEstudiantesCurso] = useState([]);
    const [loadingFiltros, setLoadingFiltros] = useState(false);
    const [loadingEstudiantesCurso, setLoadingEstudiantesCurso] = useState(false);

    useEffect(() => {
        if (!acceder || (rolNum >= 5 && rolNum !== 10)) {
            navigate('/');
            return;
        }
        cargarTodosLosEstudiantes();
        cargarFiltrosCurso();
    }, [acceder, rolNum, navigate]);

    // Cargar la nómina general de estudiantes para la búsqueda directa
    const cargarTodosLosEstudiantes = async () => {
        setLoadingEstudiantesGeneral(true);
        try {
            const loggeduserNivelId = localStorage.getItem('loggeduserNivelId');
            const res = await axios.post(URL_LISTAR, {
                id_rol: 7,
                id_nivel: loggeduserNivelId
            });
            if (Array.isArray(res.data)) {
                setTodosEstudiantes(res.data);
            } else {
                setTodosEstudiantes([]);
            }
        } catch (e) {
            console.error("Error al cargar lista general de estudiantes:", e);
            setTodosEstudiantes([]);
        } finally {
            setLoadingEstudiantesGeneral(false);
        }
    };

    // Cargar filtros de curso
    const cargarFiltrosCurso = async () => {
        setLoadingFiltros(true);
        try {
            const loggeduserNivelId = localStorage.getItem('loggeduserNivelId');
            const res = await axios.post(URL_REPORTES, {
                modo: 'filtrosOpciones',
                id_nivel: loggeduserNivelId
            });
            if (!res.data.error) {
                const comb = res.data.combinaciones || [];
                setCombinaciones(comb);
                const ciclosUnicos = [...new Set(comb.map(c => c.ciclo))].sort((a, b) => b.localeCompare(a));
                setOpcionesCiclos(ciclosUnicos);
                if (ciclosUnicos.length > 0) {
                    setFiltroCiclo(ciclosUnicos[0]);
                }
            }
        } catch (e) {
            console.error("Error al cargar filtros de cursos:", e);
        } finally {
            setLoadingFiltros(false);
        }
    };

    // Cascada de filtros por curso
    useEffect(() => {
        if (combinaciones.length === 0) return;

        let combisNivel = combinaciones;
        if (filtroCiclo) combisNivel = combisNivel.filter(c => String(c.ciclo) === String(filtroCiclo));

        const nivelesMap = new Map();
        combisNivel.forEach(c => nivelesMap.set(c.nivel_id, c.nivel_nombre));
        const nivelesArr = Array.from(nivelesMap, ([id, nombre]) => ({ id, nombre }));
        setOpcionesNiveles(nivelesArr);

        let combisOrden = combisNivel;
        if (filtroNivel) combisOrden = combisOrden.filter(c => String(c.nivel_id) === String(filtroNivel));

        const ordenesUnicos = [...new Set(combisOrden.map(c => c.orden))].sort((a, b) => a - b);
        setOpcionesOrdenes(ordenesUnicos);

        let combisSeccion = combisOrden;
        if (filtroOrden) combisSeccion = combisSeccion.filter(c => String(c.orden) === String(filtroOrden));

        const seccionesUnicas = [...new Set(combisSeccion.map(c => c.division))].sort((a, b) => a.localeCompare(b));
        setOpcionesSecciones(seccionesUnicas);
    }, [combinaciones, filtroCiclo, filtroNivel, filtroOrden]);

    // Cargar estudiantes cuando se completa la selección del curso
    useEffect(() => {
        if (!filtroCiclo || !filtroNivel || !filtroOrden || !filtroSeccion) {
            setEstudiantesCurso([]);
            return;
        }

        cargarEstudiantesPorCurso();
    }, [filtroCiclo, filtroNivel, filtroOrden, filtroSeccion]);

    const cargarEstudiantesPorCurso = async () => {
        setLoadingEstudiantesCurso(true);
        try {
            const loggeduserNivelId = localStorage.getItem('loggeduserNivelId');
            const resEst = await axios.post(URL_REPORTES, {
                modo: 'estudiantesPorCurso',
                id_nivel: loggeduserNivelId,
                ciclo: filtroCiclo,
                nivel: filtroNivel,
                orden: filtroOrden,
                seccion: filtroSeccion,
                espacio: 'general'
            });

            if (resEst.data && !resEst.data.error && resEst.data.datos) {
                const unicosMap = new Map();
                resEst.data.datos.forEach(d => {
                    const estId = d.estudiante_id || d.id;
                    if (estId && !unicosMap.has(estId)) {
                        unicosMap.set(estId, {
                            id: estId,
                            nombre: d.nombre,
                            apellido: d.apellido,
                            documento: d.documento
                        });
                    }
                });
                const listaEst = Array.from(unicosMap.values()).sort((a, b) => a.apellido.localeCompare(b.apellido));
                setEstudiantesCurso(listaEst);
            } else {
                setEstudiantesCurso([]);
            }
        } catch (error) {
            console.error("Error al cargar estudiantes del curso:", error);
            setEstudiantesCurso([]);
        } finally {
            setLoadingEstudiantesCurso(false);
        }
    };

    // Filtrar estudiantes por texto (nombre / apellido / DNI)
    const estudiantesFiltrados = !busquedaTexto.trim()
        ? todosEstudiantes.slice(0, 20) // limitar primer render si no hay filtro
        : todosEstudiantes.filter(est => {
            const term = busquedaTexto.toLowerCase();
            const nom = (est.nombre || '').toLowerCase();
            const ape = (est.apellido || '').toLowerCase();
            const doc = String(est.documento || '').toLowerCase();
            return nom.includes(term) || ape.includes(term) || `${ape} ${nom}`.includes(term) || doc.includes(term);
        });

    return (
        <div className="container-principal">
            <div className="container-fluid py-3 px-md-4">
                {/* Encabezado Principal */}
                <div className="card shadow-sm border-0 mb-4 bg-white rounded-3">
                    <div className="card-body d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3">
                        <div>
                            <h4 className="fw-bold mb-0 text-dark">
                                <i className="fa-solid fa-user-check text-primary me-2"></i>
                                Inasistencias por Estudiante — Preceptoría
                            </h4>
                            <p className="text-muted small mb-0 mt-1">
                                Seleccione o busque un estudiante para ver el detalle e historial completo de inasistencias y tardanzas.
                            </p>
                        </div>

                        {estudianteSeleccionado && (
                            <button
                                type="button"
                                className="btn btn-outline-primary btn-sm rounded-pill shadow-sm"
                                onClick={() => setEstudianteSeleccionado(null)}
                            >
                                <i className="fa-solid fa-magnifying-glass me-1"></i>
                                Seleccionar otro estudiante
                            </button>
                        )}
                    </div>
                </div>

                {/* SI HAY UN ESTUDIANTE SELECCIONADO: MOSTRAR BANNER DE PERFIL Y LA VISTA DETALLADA DE ASISTENCIA */}
                {estudianteSeleccionado ? (
                    <div>
                        {/* Banner del Estudiante Seleccionado */}
                        <div className="card shadow-sm border-0 bg-primary bg-gradient text-white mb-4 rounded-3">
                            <div className="card-body p-3 d-flex flex-column flex-sm-row justify-content-between align-items-sm-center gap-3">
                                <div className="d-flex align-items-center gap-3">
                                    <PerfilLogo usuario={estudianteSeleccionado} version="extendida+" configuracion={configuracion} />
                                </div>
                                <div className="d-flex gap-2">
                                    <button
                                        type="button"
                                        className="btn btn-light btn-sm text-primary fw-bold"
                                        onClick={() => setEstudianteSeleccionado(null)}
                                    >
                                        <i className="fa-solid fa-arrow-left me-1"></i>
                                        Volver a la Búsqueda
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Componente idéntico al del estudiante/tutor */}
                        <AsistenciaDetalleEstudiante
                            acceder={acceder}
                            configuracion={configuracion}
                            studentIdProp={estudianteSeleccionado.id}
                            studentDataProp={estudianteSeleccionado}
                        />
                    </div>
                ) : (
                    /* PANTALLA DE BÚSQUEDA Y SELECCIÓN DE ESTUDIANTE */
                    <div className="card shadow-sm border-0 rounded-3">
                        <div className="card-header bg-white border-bottom-0 pt-3 pb-0 px-3 px-md-4">
                            {/* Pestañas para elegir modo de búsqueda */}
                            <ul className="nav nav-tabs card-header-tabs border-bottom-0">
                                <li className="nav-item">
                                    <button
                                        className={`nav-link fw-bold ${modoBusqueda === 'estudiante' ? 'active text-primary border-bottom border-primary border-3' : 'text-muted'}`}
                                        onClick={() => setModoBusqueda('estudiante')}
                                    >
                                        <i className="fa-solid fa-magnifying-glass me-2"></i>
                                        Buscar por Estudiante (Nombre / DNI)
                                    </button>
                                </li>
                                <li className="nav-item">
                                    <button
                                        className={`nav-link fw-bold ${modoBusqueda === 'curso' ? 'active text-primary border-bottom border-primary border-3' : 'text-muted'}`}
                                        onClick={() => setModoBusqueda('curso')}
                                    >
                                        <i className="fa-solid fa-users me-2"></i>
                                        Buscar por Curso / División
                                    </button>
                                </li>
                            </ul>
                        </div>

                        <div className="card-body p-3 p-md-4">
                            {/* MODALIDAD 1: BÚSQUEDA DIRECTA POR ESTUDIANTE */}
                            {modoBusqueda === 'estudiante' && (
                                <div>
                                    <div className="row justify-content-center mb-4">
                                        <div className="col-12 col-md-8 col-lg-6">
                                            <label htmlFor="searchStudentInput" className="form-label small fw-bold text-muted">
                                                Ingrese el nombre, apellido o DNI del estudiante:
                                            </label>
                                            <div className="input-group shadow-sm">
                                                <span className="input-group-text bg-light border-end-0">
                                                    <i className="fa-solid fa-magnifying-glass text-primary"></i>
                                                </span>
                                                <input
                                                    id="searchStudentInput"
                                                    type="text"
                                                    className="form-control border-start-0 ps-0"
                                                    placeholder="Ej: Pérez, María / 45123456..."
                                                    value={busquedaTexto}
                                                    onChange={(e) => setBusquedaTexto(e.target.value)}
                                                    autoFocus
                                                />
                                                {busquedaTexto && (
                                                    <button
                                                        type="button"
                                                        className="btn btn-outline-secondary"
                                                        onClick={() => setBusquedaTexto('')}
                                                    >
                                                        <i className="fa-solid fa-xmark"></i>
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {loadingEstudiantesGeneral ? (
                                        <div className="text-center py-5">
                                            <Espera />
                                            <p className="text-muted small mt-2">Cargando nómina de estudiantes...</p>
                                        </div>
                                    ) : estudiantesFiltrados.length > 0 ? (
                                        <div className="row g-3">
                                            {estudiantesFiltrados.map((est) => (
                                                <div key={est.id} className="col-12 col-md-6 col-lg-4">
                                                    <div className="card h-100 border shadow-sm hover-shadow transition-all rounded-3 p-2">
                                                        <div className="card-body p-2 d-flex align-items-center justify-content-between">
                                                            <div className="d-flex align-items-center gap-2 overflow-hidden">
                                                                <PerfilLogo usuario={est} version="extendida+" configuracion={configuracion} />
                                                            </div>
                                                            <button
                                                                type="button"
                                                                className="btn btn-primary btn-sm text-nowrap rounded-pill ms-2 shadow-sm"
                                                                onClick={() => setEstudianteSeleccionado(est)}
                                                            >
                                                                <i className="fa-solid fa-chart-pie me-1"></i>
                                                                Ver Asistencia
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="alert alert-warning text-center py-4 rounded-3" role="alert">
                                            <i className="fa-solid fa-circle-exclamation me-2 fs-5"></i>
                                            No se encontraron estudiantes que coincidan con la búsqueda "<strong>{busquedaTexto}</strong>".
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* MODALIDAD 2: BÚSQUEDA POR CURSO / DIVISIÓN */}
                            {modoBusqueda === 'curso' && (
                                <div>
                                    <div className="card bg-light border mb-4">
                                        <div className="card-body p-3">
                                            <h6 className="fw-bold text-dark mb-3">
                                                <i className="fa-solid fa-filter text-primary me-2"></i>
                                                Seleccionar División y Curso
                                            </h6>
                                            {loadingFiltros ? (
                                                <div className="text-center py-3"><Espera /></div>
                                            ) : (
                                                <div className="row g-3">
                                                    <div className="col-12 col-md-3">
                                                        <label className="form-label small fw-bold">Ciclo Lectivo</label>
                                                        <select
                                                            className="form-select form-select-sm"
                                                            value={filtroCiclo}
                                                            onChange={e => {
                                                                setFiltroCiclo(e.target.value);
                                                                setFiltroNivel(''); setFiltroOrden(''); setFiltroSeccion('');
                                                            }}
                                                        >
                                                            <option value="">Seleccione Ciclo</option>
                                                            {opcionesCiclos.map(c => <option key={c} value={c}>{c}</option>)}
                                                        </select>
                                                    </div>

                                                    <div className="col-12 col-md-3">
                                                        <label className="form-label small fw-bold">Nivel / Formación</label>
                                                        <select
                                                            className="form-select form-select-sm"
                                                            value={filtroNivel}
                                                            onChange={e => {
                                                                setFiltroNivel(e.target.value);
                                                                setFiltroOrden(''); setFiltroSeccion('');
                                                            }}
                                                        >
                                                            <option value="">Todos los niveles</option>
                                                            {opcionesNiveles.map(n => <option key={n.id} value={n.id}>{n.nombre}</option>)}
                                                        </select>
                                                    </div>

                                                    <div className="col-12 col-md-3">
                                                        <label className="form-label small fw-bold">Año / Orden</label>
                                                        <select
                                                            className="form-select form-select-sm"
                                                            value={filtroOrden}
                                                            onChange={e => {
                                                                setFiltroOrden(e.target.value);
                                                                setFiltroSeccion('');
                                                            }}
                                                        >
                                                            <option value="">Todos los años</option>
                                                            {opcionesOrdenes.map(o => <option key={o} value={o}>{o}</option>)}
                                                        </select>
                                                    </div>

                                                    <div className="col-12 col-md-3">
                                                        <label className="form-label small fw-bold">División / Sección</label>
                                                        <select
                                                            className="form-select form-select-sm fw-bold border-primary"
                                                            value={filtroSeccion}
                                                            onChange={e => setFiltroSeccion(e.target.value)}
                                                        >
                                                            <option value="">Todas las divisiones</option>
                                                            {opcionesSecciones.map(s => <option key={s} value={s}>{s}</option>)}
                                                        </select>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Listado de Estudiantes del Curso Seleccionado */}
                                    {filtroCiclo && filtroNivel && filtroOrden && filtroSeccion ? (
                                        loadingEstudiantesCurso ? (
                                            <div className="text-center py-5">
                                                <Espera />
                                                <p className="text-muted small mt-2">Cargando estudiantes de {filtroOrden}° División {filtroSeccion}...</p>
                                            </div>
                                        ) : estudiantesCurso.length > 0 ? (
                                            <div>
                                                <div className="d-flex justify-content-between align-items-center mb-3">
                                                    <h6 className="fw-bold text-dark m-0">
                                                        <i className="fa-solid fa-user-group text-primary me-2"></i>
                                                        Estudiantes de {filtroOrden}° {filtroSeccion} ({estudiantesCurso.length})
                                                    </h6>
                                                </div>
                                                <div className="row g-3">
                                                    {estudiantesCurso.map((est) => (
                                                        <div key={est.id} className="col-12 col-md-6 col-lg-4">
                                                            <div className="card h-100 border shadow-sm rounded-3 p-2">
                                                                <div className="card-body p-2 d-flex align-items-center justify-content-between">
                                                                    <div className="d-flex align-items-center gap-2 overflow-hidden">
                                                                        <PerfilLogo usuario={est} version="extendida+" configuracion={configuracion} />
                                                                    </div>
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-primary btn-sm text-nowrap rounded-pill ms-2 shadow-sm"
                                                                        onClick={() => setEstudianteSeleccionado(est)}
                                                                    >
                                                                        <i className="fa-solid fa-chart-pie me-1"></i>
                                                                        Ver Asistencia
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="alert alert-info text-center py-4 rounded-3">
                                                <i className="fa-solid fa-info-circle me-2 fs-5"></i>
                                                No se encontraron estudiantes registrados en {filtroOrden}° División {filtroSeccion}.
                                            </div>
                                        )
                                    ) : (
                                        <div className="text-center py-4 text-muted">
                                            <i className="fa-solid fa-hand-pointer fs-3 mb-2 text-primary opacity-50"></i>
                                            <p className="small mb-0">Seleccione el Nivel, Año y División para ver los estudiantes del curso.</p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default InasistenciasEstudiantePreceptor;
